import React, { useState, useEffect, useContext } from "react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db } from "../../../utils/firebase"

import styles from "./pricing.module.scss"

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Pricing — a clean breakdown of the booking's price lines plus the overall
 * total. Reads price docs from bookings/{id}/prices in the order set on the
 * booking, with the holiday total coming from margins.customer_price (via the
 * booking provider).
 */
export default function Pricing() {
	const { bookingID, pricesOrder, totalCost } = useContext(BookingContext)

	const [lines, setLines] = useState([])
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!bookingID) return
		let cancelled = false
		setLoading(true)

		const load = async () => {
			try {
				const docs = await Promise.all((pricesOrder || []).map((pid) => db.doc(`bookings/${bookingID}/prices/${pid}`).get()))
				const list = docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))
				if (!cancelled) setLines(list)
			} catch (err) {
				console.log("Pricing load failed:", err)
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		load()
		return () => {
			cancelled = true
		}
	}, [bookingID, pricesOrder])

	const total = Number(totalCost || 0)

	return (
		<div className={styles.pricing}>
			<header className={styles.head}>
				<h1>Pricing</h1>
				<p>Here's what's included in your booking.</p>
			</header>

			<section className={styles.card}>
				{loading ? (
					<p className={styles.empty}>Loading your pricing breakdown…</p>
				) : lines.length === 0 ? (
					<p className={styles.empty}>No pricing lines on this booking yet. Contact your agent for a breakdown.</p>
				) : (
					<>
						<ul className={styles.lines}>
							{lines.map((line) => (
								<li
									key={line.id}
									className={styles.line}>
									<span className={styles.lineName}>{line.name || "Item"}</span>
									<span className={styles.lineMeta}>
										{line.quantity ? <span className={styles.qty}>{line.quantity} ×</span> : null}
										<span className={styles.price}>£{formatGbp(line.price)}</span>
									</span>
								</li>
							))}
						</ul>

						{total > 0 ? (
							<div className={styles.total}>
								<span>Total holiday cost</span>
								<strong>£{formatGbp(total)}</strong>
							</div>
						) : null}
					</>
				)}
			</section>
		</div>
	)
}
